/**
 * Étape 5 — Coordonnées du dirigeant d'exploitation.
 *
 * Champs : prénom, nom, e-mail, téléphone (international), adresse, CP.
 * Soumet le lead à l'API avec source='diagnostic' + segment='agri'.
 */

import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, AtSign, CheckCircle2, Loader2, Lock, MapPin, User } from 'lucide-react';
import { z } from 'zod';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { PhoneInput, type PhoneInputValue } from '../ComparateurFunnel/PhoneInput';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import { apiFetch, ApiError } from '../../../lib/api';
import { captureEvent } from '../../../lib/analytics';
import {
  trackSubmitStart,
  trackSubmitSuccess,
  trackSubmitFailure,
  readAttribution,
} from '../../../lib/tracking';
import { TrustBar } from '../../conversion/TrustBar';
import { agriStepCounterLabel, useAgriStore } from './useAgriStore';
import { computeAgriEstimation } from './compute';
import { buildEstimateLine } from '@shared/lib/estimate-amount';

const contactSchema = z.object({
  firstName: z.string().min(2, 'Au moins 2 caractères').max(60).trim(),
  lastName: z.string().min(2, 'Au moins 2 caractères').max(60).trim(),
  email: z.string().email('Adresse e-mail invalide').trim(),
  phoneNational: z
    .string()
    .max(20, 'Numéro trop long')
    .refine((v) => v === '' || v.replace(/\D/g, '').length >= 6, 'Numéro trop court')
    .optional(),
  address: z.string().min(4, 'Veuillez préciser votre adresse').max(120),
  postalCode: z.string().regex(/^\d{5}$/, 'Code postal sur 5 chiffres'),
});

type ContactKey = keyof z.infer<typeof contactSchema>;

export function Step5Contact() {
  const company = useAgriStore((s) => s.company);
  const contact = useAgriStore((s) => s.contact);
  const submitting = useAgriStore((s) => s.submitting);
  const submitError = useAgriStore((s) => s.submitError);
  const patchContact = useAgriStore((s) => s.patchContact);
  const setSubmitting = useAgriStore((s) => s.setSubmitting);
  const setSubmitError = useAgriStore((s) => s.setSubmitError);
  const setLeadId = useAgriStore((s) => s.setLeadId);
  const goNext = useAgriStore((s) => s.goNext);
  const counter = useAgriStore(agriStepCounterLabel);

  const [issues, setIssues] = useState<Partial<Record<ContactKey, string>>>({});
  const [consentMarketing, setConsentMarketing] = useState(false);

  const phone: PhoneInputValue = {
    countryIso2: contact.phoneCountry,
    countryDial: contact.phoneDial,
    national: contact.phoneNational,
  };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);

    const candidate = {
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phoneNational: contact.phoneNational?.replace(/\D/g, '') || undefined,
      address: contact.address,
      postalCode: contact.postalCode,
    };

    const result = contactSchema.safeParse(candidate);
    if (!result.success) {
      const errs: Partial<Record<ContactKey, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as ContactKey | undefined;
        if (key) errs[key] = issue.message;
      }
      setIssues(errs);
      return;
    }
    setIssues({});

    const state = useAgriStore.getState();
    const phoneFull = result.data.phoneNational
      ? `+${contact.phoneDial}${result.data.phoneNational}`
      : undefined;
    const estimation = computeAgriEstimation(state);
    const message = [
      estimation.primeMax > 0 ? buildEstimateLine(estimation.primeMin, estimation.primeMax) : null,
      `Orientation: ${state.orientation ?? '-'}`,
      company
        ? `Exploitation: ${company.name} (SIRET ${company.siret}, NAF ${company.naf ?? '-'})`
        : null,
      state.serres.has
        ? `Serres: ${state.serres.type ?? '-'} · ${state.serres.surfaceM2 ?? '?'} m² · chauffée=${state.serres.chauffee ?? '?'} · ${state.serres.combustible ?? ''}`
        : 'Serres: non',
      state.elevage.has
        ? `Élevage: ${state.elevage.types.join(', ')} · effectif ${state.elevage.effectif ?? '?'}`
        : 'Élevage: non',
      state.stockage.has
        ? `Stockage: ${state.stockage.usages.join(', ')} · ${state.stockage.surfaceM2 ?? '?'} m² · réfrigéré=${state.stockage.refrigere ?? '?'}`
        : 'Stockage: non',
      `Équipements: ${state.equipements.join(', ') || '-'}`,
      `Travaux: ${state.travaux.join(', ')}`,
      estimation.fichesEligibles.length > 0
        ? `Fiches CEE: ${estimation.fichesEligibles.join(', ')}`
        : null,
      `Adresse: ${result.data.address}, ${result.data.postalCode}`,
    ]
      .filter(Boolean)
      .join('\n');

    const agri_ctx = {
      funnelId: 'agri_diagnostic' as const,
      segment: 'agri',
      intent: 'cee',
      source: 'diagnostic',
    };
    trackSubmitStart(agri_ctx, {
      orientation: state.orientation,
      travaux_count: state.travaux.length,
    });
    setSubmitting(true);
    try {
      const res = await apiFetch<{ id: string }>('/api/leads', {
        json: {
          email: result.data.email,
          ...(phoneFull ? { phone: phoneFull } : {}),
          firstName: result.data.firstName,
          lastName: result.data.lastName,
          message,
          source: 'diagnostic',
          segment: 'agri',
          intent: 'cee',
          consentMarketing,
          ...readAttribution(),
        },
      });

      captureEvent('agri_diagnostic_submit', {
        tool: 'diagnostic-cee-agricole',
        orientation: state.orientation,
        has_serres: state.serres.has ?? false,
        has_elevage: state.elevage.has ?? false,
        has_stockage: state.stockage.has ?? false,
        travaux_count: state.travaux.length,
        estimate_min: estimation.primeMin,
        estimate_max: estimation.primeMax,
      });
      trackSubmitSuccess(agri_ctx, res.id, { email: result.data.email });

      setLeadId(res.id);
      goNext();
    } catch (err) {
      trackSubmitFailure(agri_ctx, err instanceof ApiError ? 'api' : 'unknown');
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : "Une erreur est survenue lors de l'envoi. Réessayez.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">{counter}</p>
      <h2 className="h-section mt-2">Vos coordonnées</h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        Un conseiller énergie agricole vous recontacte sous 24&nbsp;h ouvrées avec votre estimation
        détaillée et le plan de dépôt CEE.
      </p>

      {company && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50/60 p-3.5">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" strokeWidth={2} />
          <p className="text-[12.5px] text-brand-900">
            Dossier de <strong>{company.name}</strong>
            {company.naf && <span className="text-brand-700"> · NAF {company.naf}</span>}
          </p>
        </div>
      )}

      {submitError && (
        <Alert variant="error" className="mt-4">
          <AlertDescription className="text-[13px]">{submitError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        {/* Prénom + Nom */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="agri-firstname"
            label="Prénom"
            error={issues.firstName}
            icon={<User className="h-4 w-4" />}
          >
            <Input
              id="agri-firstname"
              autoComplete="given-name"
              required
              value={contact.firstName}
              onChange={(e) => patchContact({ firstName: e.target.value })}
              placeholder="Jean"
              className="h-12 rounded-xl border-2 border-stone-200 pl-10 text-[15px] focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
              aria-invalid={!!issues.firstName}
            />
          </Field>

          <Field id="agri-lastname" label="Nom" error={issues.lastName}>
            <Input
              id="agri-lastname"
              autoComplete="family-name"
              required
              value={contact.lastName}
              onChange={(e) => patchContact({ lastName: e.target.value })}
              placeholder="Dupont"
              className="h-12 rounded-xl border-2 border-stone-200 text-[15px] focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
              aria-invalid={!!issues.lastName}
            />
          </Field>
        </div>

        {/* E-mail */}
        <Field
          id="agri-email"
          label="E-mail"
          hint="Pour recevoir votre estimation détaillée"
          error={issues.email}
          icon={<AtSign className="h-4 w-4" />}
        >
          <Input
            id="agri-email"
            type="email"
            autoComplete="email"
            required
            value={contact.email}
            onChange={(e) => patchContact({ email: e.target.value })}
            placeholder="vous@email.com"
            className="h-12 rounded-xl border-2 border-stone-200 pl-10 text-[15px] focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
            aria-invalid={!!issues.email}
          />
        </Field>

        {/* Téléphone */}
        <div>
          <Label htmlFor="agri-phone" className="text-[13px] font-semibold text-ink">
            Téléphone <span className="font-normal text-stone-400">(avec indicatif)</span>
          </Label>
          <div className="mt-1.5">
            <PhoneInput
              id="agri-phone"
              value={phone}
              onChange={(v) =>
                patchContact({
                  phoneCountry: v.countryIso2,
                  phoneDial: v.countryDial,
                  phoneNational: v.national,
                })
              }
              error={issues.phoneNational}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-stone-400">
            Optionnel — accélère le traitement de votre dossier CEE.
          </p>
        </div>

        {/* Adresse + CP */}
        <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
          <Field
            id="agri-address"
            label="Adresse de l'exploitation"
            error={issues.address}
            icon={<MapPin className="h-4 w-4" />}
          >
            <Input
              id="agri-address"
              autoComplete="street-address"
              required
              value={contact.address}
              onChange={(e) => patchContact({ address: e.target.value })}
              placeholder="Lieu-dit, route, hameau…"
              className="h-12 rounded-xl border-2 border-stone-200 pl-10 text-[15px] focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
              aria-invalid={!!issues.address}
            />
          </Field>

          <Field id="agri-cp" label="Code postal" error={issues.postalCode}>
            <Input
              id="agri-cp"
              inputMode="numeric"
              autoComplete="postal-code"
              required
              maxLength={5}
              value={contact.postalCode}
              onChange={(e) =>
                patchContact({
                  postalCode: e.target.value.replace(/\D/g, '').slice(0, 5),
                })
              }
              placeholder="Code postal"
              className="h-12 rounded-xl border-2 border-stone-200 text-center text-[15px] tabular-nums focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
              aria-invalid={!!issues.postalCode}
            />
          </Field>
        </div>

        {/* Marketing opt-in */}
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-stone-200 bg-stone-50/50 p-3 transition-colors hover:bg-stone-50">
          <span
            className={cn(
              'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors',
              consentMarketing ? 'border-brand-500 bg-brand-500' : 'border-stone-300 bg-white',
            )}
          >
            {consentMarketing && (
              <motion.span
                initial={{ opacity: 0, scale: 0.4 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15, ease: easeStripe }}
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={2.4} />
              </motion.span>
            )}
            <input
              type="checkbox"
              checked={consentMarketing}
              onChange={(e) => setConsentMarketing(e.target.checked)}
              className="sr-only"
              aria-label="Recevoir l'actualité Solution Éco France pour les agriculteurs"
            />
          </span>
          <span className="text-[12px] leading-relaxed text-stone-600">
            J'accepte de recevoir l'actualité des aides agricoles (CEE, PCAE, FranceAgriMer) et les
            offres de Solution Éco France. Désabonnement en 1 clic.{' '}
            <span className="text-stone-400">(optionnel)</span>
          </span>
        </label>

        <div className="flex items-start gap-2 rounded-lg border border-stone-200 bg-stone-50/60 px-3 py-2.5">
          <Lock
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400"
            strokeWidth={1.8}
            aria-hidden
          />
          <p className="text-[11.5px] leading-relaxed text-stone-500">
            Données traitées par Solution Éco France pour vous transmettre votre estimation et
            préparer le dépôt de votre dossier CEE agricole (RGPD art. 6.1.b).{' '}
            <a
              href="/legal/politique-confidentialite"
              className="font-medium text-brand-700 hover:underline"
            >
              Politique de confidentialité
            </a>
          </p>
        </div>

        <Button
          type="submit"
          variant="brand"
          size="xl"
          disabled={submitting}
          className="w-full gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Envoi en cours…
            </>
          ) : (
            <>
              Voir mon estimation CEE
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>

        <TrustBar variant="inline" max={4} className="pt-1" />
      </form>
    </div>
  );
}

function Field({
  id,
  label,
  hint,
  error,
  icon,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={id} className="text-[13px] font-semibold text-ink">
        {label}
      </Label>
      <div className="relative mt-1.5">
        {icon && (
          <span
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
          >
            {icon}
          </span>
        )}
        {children}
      </div>
      {error ? (
        <p className="mt-1 text-[12px] font-medium text-red-600" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-[11px] text-stone-400">{hint}</p>
      ) : null}
    </div>
  );
}
